import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Shuffle, Plus, X, Search } from "lucide-react";
import { toast } from "sonner";
import { api, gbp, ukDate, errorMessage } from "../../lib/api";
import { shortLabel } from "./PriceCards";
import { Button } from "../ui/button";

function AddAlternative({ product, excludeIds, onAdded, onClose }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  useEffect(() => {
    if (q.trim().length < 2) return setResults([]);
    const t = setTimeout(() => api.get("/supplier-products", { params: { q: q.trim() } }).then((r) => {
      const list = Array.isArray(r.data) ? r.data : r.data.products || [];
      setResults(list.filter((p) => !excludeIds.has(p.id)).slice(0, 8));
    }).catch(() => {}), 250);
    return () => clearTimeout(t);
  }, [q, excludeIds]);
  const add = async (p) => {
    try {
      await api.post("/product-matches/alternative", { a: product.id, b: p.id });
      toast.success(`${p.description} added as an alternative`);
      onAdded();
    } catch (e) { toast.error(errorMessage(e)); }
  };
  return (
    <div className="mt-2 rounded-lg border border-amber-200 bg-amber-50/40 p-3" data-testid="alternative-add-form">
      <div className="flex items-center gap-2">
        <Search className="h-4 w-4 text-slate-400" />
        <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search any supplier product, e.g. table salt" className="flex-1 bg-transparent text-sm focus:outline-none" data-testid="alternative-search-input" />
        <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="alternative-add-cancel"><X className="h-4 w-4" /></button>
      </div>
      {results.length > 0 && (
        <ul className="mt-2 divide-y divide-amber-100" data-testid="alternative-search-results">
          {results.map((p) => (
            <li key={p.id} className="flex items-center justify-between gap-2 py-1.5 text-sm">
              <span className="min-w-0"><span className="block truncate font-medium text-slate-800">{p.description}</span><span className="block text-xs text-slate-500">{p.supplier_name} · {p.pack_size || "—"} · {p.latest_price != null ? gbp(p.latest_price) : "no price"}</span></span>
              <Button size="sm" variant="outline" onClick={() => add(p)} data-testid={`alternative-add-${p.id}`}><Plus className="h-3.5 w-3.5 mr-1" /> Add</Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function Alternatives({ product, alternatives, cheaper, canEdit, onChanged }) {
  const [adding, setAdding] = useState(false);
  const excludeIds = new Set([product.id, ...alternatives.flatMap((a) => a.merged_ids || [a.supplier_product_id])]);
  const remove = async (a) => {
    try {
      await api.post("/product-matches/alternative/remove", { a: product.id, b: a.supplier_product_id });
      toast.success("Alternative removed");
      onChanged();
    } catch (e) { toast.error(errorMessage(e)); }
  };
  return (
    <section className="mt-8" data-testid="alternatives-section">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
        <h2 className="text-lg font-semibold flex items-center gap-2"><Shuffle className="h-4 w-4 text-amber-600" /> Alternatives <span className="text-slate-400 font-normal text-sm" data-testid="alternatives-count">({alternatives.length})</span></h2>
        {canEdit && !adding && <Button size="sm" variant="outline" onClick={() => setAdding(true)} data-testid="alternative-add-button"><Plus className="h-4 w-4 mr-1" /> Add alternative</Button>}
      </div>
      <p className="text-xs text-slate-500 mb-2">Different products that can replace this one. They keep their own names, prices and history — only compared per kg, litre or item.</p>
      {adding && <AddAlternative product={product} excludeIds={excludeIds} onAdded={() => { setAdding(false); onChanged(); }} onClose={() => setAdding(false)} />}
      {cheaper && (
        <p className="mt-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-900" data-testid="cheaper-alternative">
          Cheaper alternative: <Link to={`/products/${cheaper.supplier_product_id}`} className="font-semibold hover:underline">{cheaper.display_name || cheaper.description}</Link> at {cheaper.supplier_name} — <span className="font-mono">{gbp(cheaper.unit_price)} {shortLabel(cheaper.unit_label)}</span> ({gbp(cheaper.price)} / {(cheaper.unit || "pack").toLowerCase()}), {cheaper.saving_pct}% less per {(cheaper.unit_label || "").replace("per ", "")}.
        </p>
      )}
      {alternatives.length > 0 && (
        <ul className="mt-2 bg-white border border-slate-200 rounded-lg divide-y divide-slate-100" data-testid="alternatives-list">
          {alternatives.map((a) => (
            <li key={a.supplier_product_id} className="flex items-center justify-between gap-3 px-4 py-2.5 text-sm" data-testid={`alternative-${a.supplier_product_id}`}>
              <span className="min-w-0">
                <Link to={`/products/${a.supplier_product_id}`} className="font-medium text-slate-900 hover:text-emerald-700 truncate block">{a.display_name || a.description}</Link>
                <span className="block text-xs text-slate-500">{a.supplier_name}{a.pack_size ? ` · ${a.pack_size}` : ""}{a.price_date ? ` · ${ukDate(a.price_date)}` : ""}</span>
              </span>
              <span className="text-right shrink-0">
                <span className="block font-mono">{a.price != null ? `${gbp(a.price)} / ${(a.unit || "pack").toLowerCase()}` : "—"}</span>
                {a.unit_price != null && a.unit_label !== "per pack" && <span className="block text-xs font-mono text-slate-500">{gbp(a.unit_price)} {shortLabel(a.unit_label)}</span>}
              </span>
              {canEdit && <button type="button" onClick={() => remove(a)} className="p-1.5 text-slate-400 hover:text-rose-600" title="Remove alternative" data-testid={`alternative-remove-${a.supplier_product_id}`}><X className="h-4 w-4" /></button>}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
